import Language from './model.js'
import { deepGet, addToCollection, convertNumberToLocale } from '../shared/helper'

export default {
  namespaced: true,
  state: {
    languages: [],
    filteredCollection: [],
    customerCollection: [],
    accountCollection: [],
    deepLCollection: [],
    usage: [],
    currentLanguage: new Language(),
    currentCustomer: null,
    allLanguagesLoaded: false,
  },
  getters: {
    getLanguageCollection: state => state.languages,
    getAllLanguagesLoaded: state => state.allLanguagesLoaded,
    getFilteredCollection: state => state.filteredCollection,
    getById: state => (id) => state.languages.find(language => language.id === id),
    getCurrentLanguage: state => state.currentLanguage,
    getCustomerCollection: state => state.customerCollection,
    getAccountCollection: state => state.accountCollection,
    getCurrentCustomer: state => state.currentCustomer,
    getUsage: state => state.usage,
    getDeepLCollection: state => state.deepLCollection,
    getDeepLById: state => (id) => state.deepLCollection.find(deepl => deepl.id === id),
  },
  actions: {
    getLanguageCollection({ commit, getters, state }, payload) {
      return this.dispatch('get', {
        path: 'v1/languages',
        ...payload
      }).then((res) => {

        const data = deepGet(res, 'body.data')
        if (data) {

          const models = data.map((language) => {
            commit('addLanguageToCollection', { languageData: language })
            return getters.getById(language.id)
          })

          // avoid when loading isos, otherwise the table has issues
          if (!payload.isoLoading) {
            commit('addLanguageToFilteredCollection', models)
          }

          return { data: models, meta: res.body.meta }
        }
        return res
      })
    },
    verifyLanguageExists({ commit, getters, state }, payload) {
      return this.dispatch('get', {
        path: 'v1/languages',
        ...payload
      }).then((res) => {

        const data = deepGet(res, 'body.data')
        if (data) {
          return { data, meta: res.body.meta }
        }
        return res
      })
    },
    getCustomerCollection({ commit }, payload) {
      const id = payload.id
      delete payload.id
      return this.dispatch('get', {
        path: 'v1/languages/' + id + '/customers',
        ...payload
      }).then((res) => {
        commit('updateCustomerCollection', res && res.body)
        return res && res.body
      })
    },
    getAccountCollection({ state, commit }, payload) {
      if (!state.currentCustomer) {
        throw Error('missing current customer')
      }
      const id = payload.id
      delete payload.id
      return this.dispatch('get', {
        path: `v1/languages/${id}/customers/${state.currentCustomer.id}/accounts`,
        ...payload
      }).then((res) => {
        commit('updateAccountCollection', res && res.body)
        return res && res.body
      })
    },
    create({ commit, getters }, model) {
      return this.dispatch('post', {
        path: 'v1/languages',
        data: model.getJSON()
      }).then((res) => {

        commit('addLanguageToCollection', { languageData: res.body && res.body.data })

        return getters.getById(res.body && res.body.data.id)
      })
    },
    updateLanguage({ commit, getters }, model) {
      return this.dispatch('put', {
        path: 'v1/languages/' + model.id,
        data: model.getJSON()
      }).then((res) => {

        commit('addLanguageToCollection', { languageData: res.body && res.body.data })

        return getters.getById(res.body && res.body.data.id)
      })
    },
    updateCustomerAccount({ state }, payload) {
      if (!state.currentCustomer) {
        throw Error('missing current customer')
      }
      return this.dispatch('put', {
        path: `v1/languages/${payload.id}/customers/${state.currentCustomer.id}/accounts`,
        data: payload
      })
    },
    deleteLanguage({ commit }, id) {
      return this.dispatch('del', {
        path: 'v1/languages/' + id
      }).then((res) => {

        commit('removeLanguageFromCollection', id)

        return res
      })
    },
    getUsage ({ commit }, payload) {
      return this.dispatch('get', {
        path: 'v1/languages/' + payload.id + '/usage',
        ...payload
      }).then((res) => {

        // Adjust numbers as early as possible
        res.body.data.forEach((account) => {
          account.mailingCount = convertNumberToLocale(account.mailingCount, this.getters.locale)
          account.articleCount = convertNumberToLocale(account.articleCount, this.getters.locale)
          commit('addUsageToCollection', account)
        })

        return res.body
      })
    },
    loadDeepLCollection({ getters, commit }, payload) {
      return this.dispatch('get', {
        path: 'v1/deeplLanguages',
      }).then((res) => {
        const data = deepGet(res, 'body.data')
        if (data) {
          const models = data.map((deeplLanguage) => {
            commit('addDeepLToCollection', { deepLData: deeplLanguage })
            return getters.getDeepLById(deeplLanguage.id)
          })
          return { data: models, meta: res.body.meta }
        }
        return res
      })
    },
  },
  mutations: {
    addLanguageToCollection(state, { languageData, opts }) {
      addToCollection(this, state.languages, languageData, Language, opts)
    },
    addLanguageToFilteredCollection(state, data) {
      state.filteredCollection = state.filteredCollection.concat(data)
    },
    removeLanguageFromCollection(state, languageId) {
      const index = state.languages.findIndex(el => el.id === languageId)

      if (index === -1) {
        console.warn(`Could not find/delete language ${languageId}`)
        return
      }

      state.languages.splice(index, 1)
    },
    addUsageToCollection(state, usageData) {
      addToCollection(this, state.usage, usageData, null, null)
    },
    clearCollection(state) {
      state.languages = []
    },
    clearFilteredCollection(state) {
      state.filteredCollection = []
    },
    clearCustomerCollection(state) {
      state.customerCollection = []
    },
    clearAccountCollection(state) {
      state.accountCollection = []
    },
    clearUsage(state) {
      state.usage = []
    },
    updateAccountCollection (state, account) {
      state.accountCollection = state.accountCollection.concat(account.data)
    },
    updateCustomerCollection (state, customers) {
      state.customerCollection = state.customerCollection.concat(customers.data)
    },
    setCurrentLanguage (state, language) {
      state.currentLanguage = language
    },
    setCurrentCustomer (state, customer) {
      state.currentCustomer = customer
    },
    resetCurrentLanguage (state) {
      state.currentLanguage = new Language()
    },
    resetCurrentCustomer (state) {
      state.currentCustomer = null
    },
    addDeepLToCollection(state, { deepLData, opts }) {
      addToCollection(this, state.deepLCollection, deepLData, null)
    },
    setAllLanguagesLoaded(state, value) {
      state.allLanguagesLoaded = value
    },
  }
}



// WEBPACK FOOTER //
// ./src/components/languages/store.js